import React, { useEffect, useRef } from "react";
import type { EChartsInstance } from "echarts-for-react";
import { BarChart3, Users, LineChart } from "lucide-react";
import { MultiSelectDropdown } from "@/shared/components/common/ui/MultiSelectDropdown";
import ContentLoader from "@/shared/components/common/ui/ContentLoader";
import CostScatterChart from "../../components/charts/weekly-cost-report/CostScatterChart";
import WeeklyCostTrendChart from "../../components/charts/weekly-cost-report/CostTrendChart";
import WeeklyCostReportHeader from "../../components/headers/WeeklyCostReportHeader";
import WeeklyCostChartPanel from "../../components/panels/WeeklyCostChartPanel";
import { useWeeklyCostData } from "../../hooks/weekly-cost/useWeeklyCostData";
import WeeklyCostInsightPanel from "../../components/panels/WeeklyCostInsightPanel";
import { getLastWeekMonday, getNextMonday, getWeekDetails } from "../../utils/weekly-cost-report/weeklyCostTimeRange.utils";
import { useLoading } from "@/shared/hooks/useLoading";
import {
  ALL_CUSTOMERS_VALUE,
  WEEKLY_COST_CUSTOMER_STORAGE_KEY,
} from "@/features/cost/constants/weekly-cost.constants";
import CostBarChart from "../../components/charts/weekly-cost-report/CostBarChart";
import { useLocalStorageObject } from "@/shared/hooks/useLocalStorageObject";

const WeeklyCostReport = () => {
  const weeklyReportRef = useRef<HTMLDivElement>(null);
  const [selectedCustomer, setSelectedCustomer] = useLocalStorageObject<string>(
    WEEKLY_COST_CUSTOMER_STORAGE_KEY,
    ALL_CUSTOMERS_VALUE,
  );

  // Initialize refs for charts
  const barChartRef = useRef<EChartsInstance | null>(null);
  const scatterChartRef = useRef<EChartsInstance | null>(null);
  const trendChartRef = useRef<EChartsInstance | null>(null);

  // Calculate week dates
  const weekStartDate = getLastWeekMonday();
  const currentWeek = getWeekDetails(weekStartDate);
  const prevDate = new Date(weekStartDate);
  prevDate.setDate(prevDate.getDate() - 7);
  const previousWeek = getWeekDetails(prevDate.toISOString());

  // Use custom hooks for data fetching and PDF export
  const {
    weeklyReportData,
    weeklyReportLoading,
    weeklyReportError,
    weeklyCustomerReportData,
    weeklyCustomerReportLoading,
    weeklyCustomerReportError,
    customerRgFilters,
    customerRgFiltersLoading,
  } = useWeeklyCostData({
    weekStartDate,
    selectedCustomer,
  });

 
  // Handle main overflow
  useEffect(() => {
    const main = weeklyReportRef.current?.closest("main") as HTMLElement | null;
    if (!main) return;
    const prev = main.style.overflow;
    main.style.overflow = "hidden";
    return () => {
      main.style.overflow = prev;
    };
  }, []);

  // Derived state
  const isAllCustomersSelected = selectedCustomer === ALL_CUSTOMERS_VALUE;
  const activeWeeklyReportData = weeklyReportData;
  const activeWeeklyReportLoading = weeklyReportLoading;
  const showLoader = useLoading(activeWeeklyReportLoading);

  const activeInsights = isAllCustomersSelected
    ? weeklyReportData?.insights ?? []
    : weeklyCustomerReportData?.insights ?? [];
  const isLoadingCustomerInsights =
    !isAllCustomersSelected && weeklyCustomerReportLoading;
  const hasWeeklyDataError =
    Boolean(weeklyReportError) ||
    (!isAllCustomersSelected && Boolean(weeklyCustomerReportError));
  const kpi = activeWeeklyReportData?.kpiSummary;

  const customerOptions = React.useMemo(
    () => [
      { label: "All", value: ALL_CUSTOMERS_VALUE },
      ...[...(customerRgFilters?.customers ?? [])]
        .sort((left, right) => left.localeCompare(right))
        .map((customer) => ({
          label: customer.toUpperCase(),
          value: customer,
        })),
    ],
    [customerRgFilters?.customers],
  );

  const handleCustomerChange = React.useCallback((customer: string) => {
    setSelectedCustomer(customer);
  }, []);

  useEffect(() => {
    if (selectedCustomer === ALL_CUSTOMERS_VALUE) return;
    if (customerRgFiltersLoading || !customerRgFilters) return;
    const isValidCustomer = (customerRgFilters.customers ?? []).includes(
      selectedCustomer,
    );
    if (!isValidCustomer) {
      setSelectedCustomer(ALL_CUSTOMERS_VALUE);
    }
  }, [
    selectedCustomer,
    customerRgFiltersLoading,
    customerRgFilters,
    setSelectedCustomer,
  ]);

  return (
    <div className="relative h-full min-h-0">
      <ContentLoader isLoading={showLoader} />
      <div
        ref={weeklyReportRef}
        className="h-full min-h-0 overflow-hidden bg-[var(--bg-canvas)] flex flex-col"
      >
        <WeeklyCostReportHeader
          currentWeek={currentWeek}
          kpi={kpi}
          // pdfLoading={pdfLoading}
      
        />
        
         <div className="border-t border-[var(--border-default)] flex-1 min-h-0 overflow-hidden grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 md:grid-rows-2 gap-2 md:gap-2 bg-[var(--bg-canvas)] p-2 md:p-2 auto-rows-[minmax(240px,auto)] md:auto-rows-fr">
          {hasWeeklyDataError ? (
            <div className="col-span-1 md:col-span-2 lg:col-span-2 h-full min-h-[260px] flex items-center justify-center text-[var(--text-tertiary)] text-sm">
              No data available
            </div>
          ) : (
            <>
              <WeeklyCostChartPanel title="Top 5 Services" icon={<BarChart3 size={18} strokeWidth={2} />}>
                <CostBarChart
                  services={activeWeeklyReportData?.services ?? []}
                  currentWeek={currentWeek}
                  previousWeek={previousWeek}
                  onReady={(instance) => {
                    barChartRef.current = instance;
                  }}
                />
              </WeeklyCostChartPanel>

              <WeeklyCostChartPanel title="Top 5 Customers" icon={<Users size={18} strokeWidth={2} />}>
                <CostScatterChart
                  customers={activeWeeklyReportData?.customers ?? []}
                  currentWeek={currentWeek}
                  previousWeek={previousWeek}
                  onReady={(instance) => {
                    scatterChartRef.current = instance;
                  }}
                />
              </WeeklyCostChartPanel>

              <WeeklyCostChartPanel title="Spend Trend" icon={<LineChart size={18} strokeWidth={2} />}>
                <WeeklyCostTrendChart
                  trend={activeWeeklyReportData?.trend ?? []}
                  currentWeek={currentWeek}
                  previousWeek={previousWeek}
                  avgDailyCost={activeWeeklyReportData?.kpiSummary?.avgDailyCost}
                  onReady={(instance) => {
                    trendChartRef.current = instance;
                  }}
                />
              </WeeklyCostChartPanel>

              <WeeklyCostChartPanel 
                title="Highlights" 
                icon={<i className="pi pi-star" style={{ fontSize: "16px" }}></i>}
                headerAction={
                  <MultiSelectDropdown
                    id="insights-customer-filter"
                    label="Customer"
                    value={[selectedCustomer]}
                    options={customerOptions}
                    onChange={(values) =>
                      handleCustomerChange(values[0] ?? ALL_CUSTOMERS_VALUE)
                    }
                    disabled={customerRgFiltersLoading}
                    showSearch
                    singleSelect
                    sx={{ width: 180 }}
                  />
                }
              >
                <WeeklyCostInsightPanel
                  insights={activeInsights}
                  customerName={
                    isAllCustomersSelected ? null : selectedCustomer
                  }
                  isLoadingCustomerInsights={isLoadingCustomerInsights}
                />
              </WeeklyCostChartPanel>
            </>
          )}
        </div>

        <div className="shrink-0 flex items-center justify-between bg-[var(--bg-surface)] px-3 md:px-[18px] py-2 md:py-[5px]">
          <span className="text-[10px] md:text-[10px] leading-relaxed text-[var(--text-tertiary)]">
            Source: Azure Cost Management &nbsp;|&nbsp; Next report:{" "}
            {getNextMonday().toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </span>
          {/* <button
            title="Generate Insights"
            className="flex items-center gap-1.5 px-2 md:px-3 py-1 rounded-[6px] bg-[var(--bg-elevated)] border border-[var(--border-subtle)] text-[10px] md:text-[11px] font-semibold text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors duration-150 cursor-pointer"
          >
            <i className="pi pi-star" style={{ fontSize: "10px" }}></i>
            Generate Insights
          </button> */}
        </div>
      </div>
    </div>
  );
};

export default WeeklyCostReport;
